import { compareStrings } from "@app/lib/i18n/format";
import type { RelativeDateBucket } from "@app/lib/utils/timestamps";
import { makeRelativeDateBucketer } from "@app/lib/utils/timestamps";
import type { PodConversationListItemType } from "@app/types/api/assistant/conversation/spaces";
import type {
  CompactionMessageType,
  ConversationForkedFromType,
  ConversationListItemType,
  ConversationWithoutContentType,
} from "@app/types/assistant/conversation";
import {
  isPodConversation,
  isReinforcedSkillNotificationMetadata,
} from "@app/types/assistant/conversation";
import { truncate } from "@app/types/shared/utils/string_utils";
import type { PodListItemType } from "@app/types/space";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { VirtuosoMessage } from "./types";
import { isZeroHeightMessage } from "./types";

const MAX_SOURCE_CONVERSATION_TITLE_LENGTH = 50;
const UNNAMED_PARENT_CONVERSATION_TITLE = msg`Unnamed parent conversation`;

type Translate = (descriptor: MessageDescriptor) => string;

function isReinforcedSkillConversation(
  conversation: ConversationListItemType
): boolean {
  return isReinforcedSkillNotificationMetadata(
    conversation.metadata?.reinforcedSkillNotification
  );
}

// We treat the conversations as unread if they are unread or have an action required
// (note that action required conversations are never marked as unread).
// Unread reinforced-skill-notification conversations are split out into their own bucket so
// the sidebar can show them in a dedicated "Skill suggestions" section above the Inbox; once
// read they fall back into the date-grouped Conversations list like any other read conversation.
export function getGroupConversationsByUnreadAndActionRequired(
  conversations: ConversationListItemType[],
  activeConversationId: string | null
) {
  return (
    conversations
      // Ensure that the conversations are always sorted by updated time as the list might have been manipulated client-side.
      .toSorted((a, b) => b.updated - a.updated)
      .reduce(
        (acc, conversation) => {
          if (conversation.unread || conversation.actionRequired) {
            if (isReinforcedSkillConversation(conversation)) {
              acc.skillSuggestionConversations.push(conversation);
            } else if (
              conversation.triggerId !== null ||
              conversation.nextWakeupAt !== null
            ) {
              acc.triggeredConversations.push(conversation);
            } else if (
              conversation.sId === activeConversationId &&
              !conversation.actionRequired
            ) {
              // The conversation being viewed cannot be unread for its viewer: the server only
              // reports it unread while the debounced mark-as-read has not landed yet. Keep it
              // out of the inbox so it does not flash there while the agent responds.
              acc.readConversations.push(conversation);
            } else {
              acc.inboxConversations.push(conversation);
            }
            return acc;
          }

          acc.readConversations.push(conversation);
          return acc;
        },
        {
          readConversations: [],
          inboxConversations: [],
          skillSuggestionConversations: [],
          triggeredConversations: [],
        } as {
          readConversations: ConversationListItemType[];
          inboxConversations: ConversationListItemType[];
          skillSuggestionConversations: ConversationListItemType[];
          triggeredConversations: ConversationListItemType[];
        }
      )
  );
}

export function getGroupConversationsByDate<
  T extends ConversationListItemType | PodConversationListItemType,
>({ conversations }: { conversations: T[] }) {
  const groups: Record<RelativeDateBucket, T[]> = {
    Today: [],
    Yesterday: [],
    "Last Week": [],
    "Last Month": [],
    "Last 12 Months": [],
    Older: [],
  };

  const bucketFor = makeRelativeDateBucketer();

  conversations.forEach((conversation: T) => {
    const bucket = bucketFor(conversation.updated ?? conversation.created);
    groups[bucket].push(conversation);
  });

  return groups;
}

export function filterReadTriggeredConversations(
  conversations: ConversationListItemType[],
  hideTriggered: boolean
): ConversationListItemType[] {
  if (!hideTriggered) {
    return conversations;
  }

  return conversations.filter(
    (c) => c.triggerId === null || c.unread || c.actionRequired
  );
}

/**
 * Group unread conversations for the sidebar inbox sections:
 * 1. Non-pod conversations first
 * 2. Pod conversations grouped by pod name
 * 3. Within each group, by updatedAt descending
 */
type UnreadConversationGroup =
  | {
      type: "non_pod";
      conversations: ConversationListItemType[];
    }
  | {
      type: "pod";
      spaceId: string;
      podName: string;
      conversations: ConversationListItemType[];
    };

export function groupUnreadConversations(
  conversations: ConversationListItemType[],
  pods: PodListItemType[]
): UnreadConversationGroup[] {
  const podNameById = new Map(pods.map((pod) => [pod.sId, pod.name]));

  const sorted = conversations.toSorted((a, b) => {
    const aIsPod = isPodConversation(a);
    const bIsPod = isPodConversation(b);

    if (aIsPod !== bIsPod) {
      return aIsPod ? 1 : -1;
    }

    if (aIsPod && bIsPod) {
      const aName = podNameById.get(a.spaceId) ?? "";
      const bName = podNameById.get(b.spaceId) ?? "";
      const nameCmp = compareStrings(aName, bName);
      if (nameCmp !== 0) {
        return nameCmp;
      }
    }

    return b.updated - a.updated;
  });

  const groups: UnreadConversationGroup[] = [];

  for (const conversation of sorted) {
    if (!isPodConversation(conversation)) {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.type === "non_pod") {
        lastGroup.conversations.push(conversation);
      } else {
        groups.push({ type: "non_pod", conversations: [conversation] });
      }
      continue;
    }

    const lastGroup = groups[groups.length - 1];
    if (
      lastGroup?.type === "pod" &&
      lastGroup.spaceId === conversation.spaceId
    ) {
      lastGroup.conversations.push(conversation);
    } else {
      groups.push({
        type: "pod",
        spaceId: conversation.spaceId,
        podName: podNameById.get(conversation.spaceId) ?? "",
        conversations: [conversation],
      });
    }
  }

  return groups;
}

export function findFirstUnreadMessageIndex(
  messages: VirtuosoMessage[],
  lastReadMs: number
): number {
  return messages.findIndex((m) => {
    // Zero-height rows deadlock VirtuosoMessageList when used as the initial
    // scroll target. Scroll to the first unread message that actually renders instead.
    if (isZeroHeightMessage(m)) {
      return false;
    }
    if (m.created > lastReadMs) {
      return true;
    }
    if (m.type === "agent_message" && (m.completedTs ?? 0) > lastReadMs) {
      return true;
    }
    return false;
  });
}

type CompactionConversationInput = Pick<
  ConversationWithoutContentType,
  "forkingData" | "sId"
>;

export function getParentConversationTitleLabel(
  parentConversation: Pick<
    ConversationForkedFromType,
    "parentConversationTitle"
  >,
  t: Translate
): string {
  return (
    parentConversation.parentConversationTitle ??
    t(UNNAMED_PARENT_CONVERSATION_TITLE)
  );
}

function getCompactionParentConversation(
  message: CompactionMessageType,
  conversation: CompactionConversationInput
): ConversationForkedFromType | null {
  if (
    !message.sourceConversationId ||
    message.sourceConversationId === conversation.sId
  ) {
    return null;
  }

  const parentConversation = conversation.forkingData?.forkedFrom;
  if (
    parentConversation?.parentConversationId !== message.sourceConversationId
  ) {
    return null;
  }

  return parentConversation;
}

export function getCompactionInProgressLabel(
  message: CompactionMessageType,
  conversation: CompactionConversationInput,
  t: Translate
): string {
  const parentConversation = getCompactionParentConversation(
    message,
    conversation
  );

  if (!parentConversation) {
    return t(msg`Compacting context, this may take a moment…`);
  }

  const parentConversationTitle = getParentConversationTitleLabel(
    parentConversation,
    t
  );
  const truncatedParentConversationTitle = truncate(
    parentConversationTitle,
    MAX_SOURCE_CONVERSATION_TITLE_LENGTH
  );

  return t(
    msg`Summarizing “${truncatedParentConversationTitle}”, this may take a moment…`
  );
}

export function getCompactionSuccessLabel(
  message: CompactionMessageType,
  conversation: CompactionConversationInput,
  t: Translate
): string {
  if (
    !message.sourceConversationId ||
    message.sourceConversationId === conversation.sId
  ) {
    return t(msg`Context compacted`);
  }

  const parentConversation = getCompactionParentConversation(
    message,
    conversation
  );
  if (parentConversation) {
    const parentConversationTitle = getParentConversationTitleLabel(
      parentConversation,
      t
    );
    const truncatedParentConversationTitle = truncate(
      parentConversationTitle,
      MAX_SOURCE_CONVERSATION_TITLE_LENGTH
    );

    return t(msg`Summarized “${truncatedParentConversationTitle}” here`);
  }

  return t(msg`Summarized another conversation here`);
}
